// ========================================
// Gratitude Item Component
// ========================================
// Individual gratitude card with actions

import { useState } from 'react';
import { format } from 'date-fns';
import { useGratitudes } from '../../hooks/useGratitudes';
import { Gratitude } from '../../types';

interface GratitudeItemProps {
  gratitude: Gratitude;
}

const GratitudeItem = ({ gratitude }: GratitudeItemProps) => {
  const { editGratitude, removeGratitude } = useGratitudes();
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState(gratitude.item);
  const [editedNotes, setEditedNotes] = useState(gratitude.notes || '');

  const formattedDate = gratitude.dateAdded
    ? format(
        typeof gratitude.dateAdded === 'object' && 'toDate' in gratitude.dateAdded
          ? gratitude.dateAdded.toDate()
          : new Date(gratitude.dateAdded),
        'MMM d, yyyy'
      )
    : '';

  const handleSave = async () => {
    try {
      await editGratitude(gratitude.id!, {
        item: editedItem,
        notes: editedNotes || undefined,
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Error editing gratitude:', err);
    }
  };

  const handleToggleActive = async () => {
    try {
      await editGratitude(gratitude.id!, { isActive: !gratitude.isActive });
    } catch (err) {
      console.error('Error toggling gratitude:', err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this gratitude?')) {
      try {
        await removeGratitude(gratitude.id!);
      } catch (err) {
        console.error('Error deleting gratitude:', err);
      }
    }
  };

  if (isEditing) {
    return (
      <div className="item-card glass-card editing">
        <div className="form-group">
          <input
            type="text"
            value={editedItem}
            onChange={(e) => setEditedItem(e.target.value)}
            className="glass-input"
          />
        </div>
        <div className="form-group">
          <textarea
            value={editedNotes}
            onChange={(e) => setEditedNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="glass-input"
            rows={2}
          />
        </div>
        <div className="item-actions">
          <button onClick={() => setIsEditing(false)} className="btn-ghost">
            Cancel
          </button>
          <button onClick={handleSave} className="glass-button">
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`item-card glass-card ${!gratitude.isActive ? 'inactive' : ''}`}>
      <div className="item-content">
        <h3 className="item-title">{gratitude.item}</h3>
        {gratitude.notes && <p className="item-notes">{gratitude.notes}</p>}
        <p className="item-date">Added {formattedDate}</p>
      </div>

      <div className="item-actions">
        <button onClick={() => setIsEditing(true)} className="btn-ghost" title="Edit">
          ✏️
        </button>
        <button onClick={handleToggleActive} className="btn-ghost" title={gratitude.isActive ? 'Archive' : 'Restore'}>
          {gratitude.isActive ? '📦' : '↩️'}
        </button>
        <button onClick={handleDelete} className="btn-ghost" title="Delete">
          🗑️
        </button>
      </div>
    </div>
  );
};

export default GratitudeItem;
